import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["table", "tbody", "pager", "pageSize", "pageInfo", "prevButton", "nextButton"]
  static values = { 
    currentPage: { type: Number, default: 1 },
    pageSize: { type: Number, default: 20 },
    totalRows: { type: Number, default: 0 }
  }

  connect() {
    this.originalRows = []
    this.filteredRows = []
    this.initializeTable()
  }

  initializeTable() {
    if (!this.hasTableTarget) return

    // Store original row data
    this.storeOriginalRows()
    
    // Add click handlers to sortable headers
    this.addSortHandlers()
    
    // Initialize pagination and render first page
    this.updateDisplay()
    
    console.log(`Table controller initialized: ${this.totalRowsValue} rows`)
  }

  storeOriginalRows() {
    const rows = Array.from(this.tbodyTarget.querySelectorAll('tr'))
    this.originalRows = rows.map((row, index) => ({
      element: row,
      originalIndex: index,
      data: this.extractRowData(row)
    }))
    this.filteredRows = [...this.originalRows]
    this.totalRowsValue = this.originalRows.length
  }

  extractRowData(row) {
    return Array.from(row.querySelectorAll('td')).map(cell => {
      const text = cell.textContent.trim()
      // Try to parse as number for better sorting
      const num = parseFloat(text.replace(/[$,]/g, ''))
      return {
        text: text,
        sortValue: isNaN(num) ? text.toLowerCase() : num,
        html: cell.innerHTML
      }
    })
  }

  addSortHandlers() {
    const headers = this.tableTarget.querySelectorAll('th[data-sortable="true"]')
    headers.forEach((header, index) => {
      header.style.cursor = 'pointer'
      header.addEventListener('click', () => this.sortByColumn(index))
      
      // Add sort indicators
      if (!header.querySelector('.sort-indicator')) {
        header.innerHTML += ' <span class="sort-indicator">⇅</span>'
      }
    })
  }

  sortByColumn(columnIndex) {
    const headers = this.tableTarget.querySelectorAll('th')
    const header = headers[columnIndex]
    
    // Determine sort direction
    const currentSort = header.dataset.sortDirection
    const newDirection = currentSort === 'asc' ? 'desc' : 'asc'
    
    // Clear all other sort indicators
    headers.forEach(h => {
      h.dataset.sortDirection = ''
      const indicator = h.querySelector('.sort-indicator')
      if (indicator) indicator.textContent = '⇅'
    })
    
    // Set new sort direction
    header.dataset.sortDirection = newDirection
    const indicator = header.querySelector('.sort-indicator')
    if (indicator) {
      indicator.textContent = newDirection === 'asc' ? '↑' : '↓'
    }
    
    // Sort the data
    this.filteredRows.sort((a, b) => {
      const aVal = a.data[columnIndex]?.sortValue || ''
      const bVal = b.data[columnIndex]?.sortValue || ''
      
      let result = 0
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        result = aVal - bVal
      } else {
        result = String(aVal).localeCompare(String(bVal))
      }
      
      return newDirection === 'desc' ? -result : result
    })
    
    // Reset to first page after sorting
    this.currentPageValue = 1
    this.updateDisplay()
  }

  updateDisplay() {
    this.updatePagination()
    this.renderCurrentPage()
  }

  updatePagination() {
    if (!this.hasPagerTarget) return

    const totalPages = Math.ceil(this.filteredRows.length / this.pageSizeValue)
    const startRow = (this.currentPageValue - 1) * this.pageSizeValue + 1
    const endRow = Math.min(this.currentPageValue * this.pageSizeValue, this.filteredRows.length)

    // Update page info
    if (this.hasPageInfoTarget) {
      this.pageInfoTarget.textContent = 
        `${startRow}-${endRow} of ${this.filteredRows.length} rows`
    }

    // Update page size selector
    if (this.hasPageSizeTarget) {
      this.pageSizeTarget.value = this.pageSizeValue
    }

    // Update navigation buttons
    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.disabled = this.currentPageValue <= 1
    }
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.disabled = this.currentPageValue >= totalPages
    }
  }

  renderCurrentPage() {
    if (!this.hasTbodyTarget) return

    // Calculate page boundaries
    const startIndex = (this.currentPageValue - 1) * this.pageSizeValue
    const endIndex = startIndex + this.pageSizeValue
    
    // Hide ALL original rows first
    this.originalRows.forEach(rowData => {
      rowData.element.style.display = 'none'
    })
    
    // Show only filtered rows for current page
    const pageRows = this.filteredRows.slice(startIndex, endIndex)
    pageRows.forEach(rowData => {
      rowData.element.style.display = ''
    })
  }

  // Event handlers for pagination controls
  previousPage() {
    if (this.currentPageValue > 1) {
      this.currentPageValue--
      this.updateDisplay()
    }
  }

  nextPage() {
    const totalPages = Math.ceil(this.filteredRows.length / this.pageSizeValue)
    if (this.currentPageValue < totalPages) {
      this.currentPageValue++
      this.updateDisplay()
    }
  }

  changePageSize(event) {
    this.pageSizeValue = parseInt(event.target.value)
    this.currentPageValue = 1 // Reset to first page
    this.updateDisplay()
  }

  // Filter functionality
  filter(event) {
    // Handle both event objects and direct string values
    const searchTerm = event?.target?.value || event || ''
    
    if (!searchTerm) {
      this.filteredRows = [...this.originalRows]
    } else {
      const term = searchTerm.toLowerCase()
      this.filteredRows = this.originalRows.filter(row => {
        return row.data.some(cell => 
          cell.text.toLowerCase().includes(term)
        )
      })
    }
    
    this.currentPageValue = 1
    this.updateDisplay()
  }

  // Method to refresh table data (for dynamic updates)
  refresh() {
    this.storeOriginalRows()
    this.updateDisplay()
  }
}